import React, { useState } from 'react';
import { api } from '@/api/client';
import type { Recipient, User } from '@/types';
import { formatTelegramDiagnostics, formatTelegramError } from '@/utils/telegramErrors';

type Props = {
  recipient?: Recipient;
  user?: User;
  recipients?: Recipient[];
  users?: User[];
  onClose: () => void;
  onDone: (message: string) => void;
};

export default function SendMessageModal({
  recipient,
  user,
  recipients = [],
  users = [],
  onClose,
  onDone,
}: Props) {
  const targetRecipient = recipient;
  const targetUser = user;
  const list = recipients.length ? recipients : users;
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState<'user' | 'all' | 'custom'>(
    targetRecipient || targetUser ? 'user' : 'user'
  );
  const [customChatId, setCustomChatId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(
    targetRecipient?.id ?? targetUser?.id ?? list[0]?.id ?? ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body: Parameters<typeof api.telegram.send>[0] = { message };
      if (targetRecipient) {
        body.user_id = targetRecipient.id;
      } else if (targetUser) {
        body.user_id = targetUser.id;
      } else if (target === 'all') {
        body.send_to_all_enabled = true;
      } else if (target === 'custom') {
        body.telegram_chat_id = customChatId;
      } else {
        body.user_id = selectedUserId;
      }

      const result = await api.telegram.send(body);
      if (result.failed > 0 && result.sent === 0) {
        const failed = result.results.find((r) => !r.success);
        setError(
          [
            formatTelegramError(failed?.error, failed?.error_code, {
              finalSendMode: failed?.sender_mode,
              deliveryProvider: failed?.delivery_provider,
              attemptedModes: failed?.attempted_modes,
              telethonExceptionType: failed?.telethon_exception_type,
              telethonExceptionMessage: failed?.telethon_exception_message,
            }),
            formatTelegramDiagnostics({
              deliveryProvider: failed?.delivery_provider,
              attemptedModes: failed?.attempted_modes,
              sessionLabel: failed?.session_label,
              entitySource: failed?.entity_source,
              telethonExceptionType: failed?.telethon_exception_type,
              telethonExceptionMessage: failed?.telethon_exception_message,
              botAttempted: failed?.bot_attempted,
            }),
          ]
            .filter(Boolean)
            .join(' '),
        );
        return;
      }
      const msg =
        result.total === 1
          ? result.sent
            ? 'Message sent'
            : 'Send failed'
          : `Sent ${result.sent}/${result.total}${result.failed ? ` (${result.failed} failed)` : ''}`;
      onDone(msg);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal card card-elevated" onClick={(e) => e.stopPropagation()} role="dialog">
        <h2>Send Telegram message</h2>
        <p className="muted modal-hint">
          {targetRecipient
            ? `To ${targetRecipient.display_name ?? targetRecipient.chat_id} (${targetRecipient.chat_id})`
            : targetUser
              ? `To ${targetUser.name ?? targetUser.telegram_chat_id} (${targetUser.telegram_chat_id})`
            : 'Send a test or custom message to verify Telegram delivery.'}
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={submit}>
          {!targetRecipient && !targetUser && (
            <div className="modal-options">
              <label className="radio-row">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'user'}
                  onChange={() => setTarget('user')}
                />
                Registered recipient
              </label>
              {target === 'user' && (
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  required
                >
                  {list.map((u) => (
                    <option key={u.id} value={u.id}>
                      {'chat_id' in u
                        ? `${u.display_name ?? u.chat_id} (${u.chat_id})`
                        : `${u.name ?? u.telegram_chat_id} (${u.telegram_chat_id})`}
                    </option>
                  ))}
                </select>
              )}

              <label className="radio-row">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'all'}
                  onChange={() => setTarget('all')}
                />
                All enabled recipients
              </label>

              <label className="radio-row">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'custom'}
                  onChange={() => setTarget('custom')}
                />
                Custom chat ID
              </label>
              {target === 'custom' && (
                <input
                  value={customChatId}
                  onChange={(e) => setCustomChatId(e.target.value)}
                  placeholder="me, @username, or numeric id"
                  required
                />
              )}
            </div>
          )}

          <label className="config-field span-2">
            <span className="config-field-label">Message</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              required
              maxLength={4096}
              placeholder="Hello from Civic Alert — Telegram test"
            />
          </label>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !message.trim()}>
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
